import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
    Calendar, CalendarOff, Coffee, ExternalLink, Info, Plus, Trash2, Pencil,
    AlertTriangle, Save, X, ChevronDown, ChevronUp, RotateCcw, Clock, Ban,
} from "lucide-react";
import { staffApi } from "@/lib/staffApi";
import { Skeleton, ErrorState } from "@/components/BookingShell";

const DAY_NAMES = { 1: "Monday", 2: "Tuesday", 3: "Wednesday", 4: "Thursday", 5: "Friday", 6: "Saturday", 7: "Sunday" };
const DAY_ORDER = [1, 2, 3, 4, 5, 6, 7];

const trimTime = (t) => (t || "").slice(0, 5);
const fmtDate = (iso) => {
    if (!iso) return "";
    try { return new Date(iso + "T00:00:00").toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }); }
    catch { return iso; }
};
const todayISO = () => new Date().toISOString().slice(0, 10);

export function StaffAvailability() {
    const [error, setError] = useState(null);
    const [payload, setPayload] = useState(null);
    const [showAdvanced, setShowAdvanced] = useState(false);

    // dialog state
    const [dayOffOpen, setDayOffOpen] = useState(false);          // add/edit day off
    const [dayOffEditing, setDayOffEditing] = useState(null);     // existing entry, or null → new
    const [specialOpen, setSpecialOpen] = useState(false);
    const [specialEditing, setSpecialEditing] = useState(null);
    const [weekDayOpen, setWeekDayOpen] = useState(false);
    const [weekDayEditing, setWeekDayEditing] = useState(null);   // { dayIndex, existing? }

    const load = () => {
        setError(null);
        staffApi.availability()
            .then((av) => {
                const emp = (av?.employees || [])[0];
                if (!emp) throw new Error("No provider found");
                return staffApi.availGetEmployee(emp.id).then((res) => ({ ...res, admin_url: av.admin_url }));
            })
            .then((next) => { setPayload(next); setError(null); })
            .catch(setError);
    };
    useEffect(load, []);

    const employee = payload?.employee;
    const daysOff = payload?.daysOff || [];
    const specialDays = payload?.specialDays || [];
    const adminUrl = payload?.admin_url;
    const employeeId = employee?.id;

    const weekMap = useMemo(() => {
        const m = {};
        (employee?.weekDayList || []).forEach((wd) => { m[wd.dayIndex] = wd; });
        return m;
    }, [employee]);

    const applyResult = (res) => {
        setPayload((prev) => prev ? { ...prev, employee: res.employee, daysOff: res.daysOff, specialDays: res.specialDays } : prev);
    };

    return (
        <div className="space-y-6" data-testid="staff-availability">
            <header className="space-y-2">
                <div className="text-[10px] uppercase tracking-[0.28em] text-gold/85">Schedule</div>
                <h1 className="font-serif font-light text-4xl text-white leading-[1.05]">Availability</h1>
                <p className="text-sm text-white/60 max-w-2xl">
                    Manage your weekly hours, breaks, days off and one-off changes.
                </p>
            </header>

            {/* If we have no cached data yet AND there's an error → full error card.
                Otherwise render the page under `payload` with an amber banner. */}
            {error && !payload && <ErrorState error={error} onRetry={load} />}
            {error && payload && (
                <div className="rounded-xl border border-amber-400/30 bg-amber-500/10 px-3 py-2 text-[11px] text-amber-100 flex items-center justify-between" data-testid="availability-stale-banner">
                    <span>Connection is slow right now. Showing the last known schedule.</span>
                    <button type="button" onClick={load} className="underline">Retry</button>
                </div>
            )}
            {!payload && !error && <Skeleton className="h-64" />}

            {payload && employee && (
                <>
                    {/* Weekly Recurring Hours */}
                    <section className="rounded-2xl glass p-5" data-testid="weekly-schedule">
                        <div className="flex items-start justify-between mb-3 gap-3 flex-wrap">
                            <div>
                                <div className="text-[10px] uppercase tracking-[0.24em] text-white/45 mb-1 flex items-center gap-2">
                                    <Calendar className="h-3 w-3 text-gold" strokeWidth={1.5} /> Weekly hours
                                </div>
                                <h2 className="font-serif text-2xl text-white">Your recurring schedule</h2>
                                <p className="text-[11px] text-white/50 mt-1">Days you're not open are hidden from public booking. Click any day to change its hours or breaks.</p>
                            </div>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            {DAY_ORDER.map((idx) => {
                                const wd = weekMap[idx];
                                const isWorking = !!wd;
                                return (
                                    <div key={idx} className={`rounded-xl border p-3 ${isWorking ? "hairline bg-white/[0.02]" : "border-white/5 bg-white/[0.01]"}`} data-testid={`weekday-${idx}`}>
                                        <div className="flex items-center justify-between">
                                            <div className="font-serif text-lg text-white">{DAY_NAMES[idx]}</div>
                                            <div className="flex items-center gap-2">
                                                <div className={`text-xs ${isWorking ? "text-gold" : "text-white/40"}`}>
                                                    {isWorking ? `${trimTime(wd.startTime)} — ${trimTime(wd.endTime)}` : "Closed"}
                                                </div>
                                                <button
                                                    onClick={() => { setWeekDayEditing({ dayIndex: idx, existing: wd || null }); setWeekDayOpen(true); }}
                                                    data-testid={`weekday-edit-${idx}`}
                                                    className="h-7 w-7 rounded-full border hairline text-white/60 hover:text-gold hover:border-gold/40 inline-flex items-center justify-center">
                                                    <Pencil className="h-3 w-3" strokeWidth={1.5} />
                                                </button>
                                            </div>
                                        </div>
                                        {isWorking && (wd.periodList || []).length > 0 && (
                                            <div className="mt-2">
                                                <div className="text-[10px] uppercase tracking-[0.16em] text-white/40 mb-1">Working periods</div>
                                                <ul className="text-xs text-white/70 space-y-0.5">
                                                    {wd.periodList.map((p, i) => {
                                                        const svcCount = (p.periodServiceList || []).length;
                                                        return (
                                                        <li key={i} className="flex items-baseline gap-2">
                                                            <span>{trimTime(p.startTime)} — {trimTime(p.endTime)}</span>
                                                            {svcCount > 0 && (
                                                                <span className="text-[10px] text-white/40">· {svcCount} services</span>
                                                            )}
                                                        </li>
                                                        );
                                                    })}
                                                </ul>
                                            </div>
                                        )}
                                        {isWorking && (wd.timeOutList || []).length > 0 && (
                                            <div className="mt-2">
                                                <div className="text-[10px] uppercase tracking-[0.16em] text-white/40 mb-1 flex items-center gap-1">
                                                    <Coffee className="h-3 w-3" /> Breaks
                                                </div>
                                                <ul className="text-xs text-white/60 space-y-0.5">
                                                    {wd.timeOutList.map((t, i) => (
                                                        <li key={i}>{trimTime(t.startTime)} — {trimTime(t.endTime)}</li>
                                                    ))}
                                                </ul>
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    </section>

                    {/* Days Off */}
                    <section className="rounded-2xl glass p-5" data-testid="days-off-section">
                        <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
                            <div>
                                <div className="text-[10px] uppercase tracking-[0.24em] text-white/45 mb-1 flex items-center gap-2">
                                    <CalendarOff className="h-3 w-3 text-gold" strokeWidth={1.5} /> Days off
                                </div>
                                <h2 className="font-serif text-2xl text-white">Block a day</h2>
                                <p className="text-[11px] text-white/50 mt-1">Days you're away, on holiday, or otherwise unavailable.</p>
                            </div>
                            <button onClick={() => { setDayOffEditing(null); setDayOffOpen(true); }}
                                    data-testid="add-day-off-btn"
                                    className="h-11 px-5 rounded-full bg-gold text-navy font-semibold text-sm inline-flex items-center gap-2">
                                <Plus className="h-4 w-4" /> Block entire day
                            </button>
                        </div>
                        {daysOff.length === 0 ? (
                            <div className="text-sm text-white/50" data-testid="days-off-empty">No days off scheduled.</div>
                        ) : (
                            <ul className="space-y-2">
                                {daysOff.map((d) => (
                                    <li key={d.id} data-testid={`day-off-${d.id}`}
                                        className="rounded-xl border hairline p-3 flex items-center justify-between gap-2 bg-white/[0.02]">
                                        <div className="min-w-0 flex-1">
                                            <div className="font-ui text-white text-sm truncate">{d.name}</div>
                                            <div className="text-[11px] text-white/50 mt-0.5">
                                                {d.startDate === d.endDate ? fmtDate(d.startDate) : `${fmtDate(d.startDate)} → ${fmtDate(d.endDate)}`}
                                                {d.repeat ? " · Repeats yearly" : ""}
                                            </div>
                                        </div>
                                        <div className="flex gap-1">
                                            <button onClick={() => { setDayOffEditing(d); setDayOffOpen(true); }}
                                                    data-testid={`day-off-edit-${d.id}`}
                                                    className="h-8 w-8 rounded-full border hairline text-white/70 hover:text-gold hover:border-gold/40 inline-flex items-center justify-center">
                                                <Pencil className="h-3 w-3" strokeWidth={1.5} />
                                            </button>
                                            <DeleteButton
                                                testId={`day-off-delete-${d.id}`}
                                                confirmText={`Remove "${d.name}"?`}
                                                onConfirm={async () => {
                                                    const r = await staffApi.availDeleteDayOff(employeeId, d.id);
                                                    applyResult(r); toast.success("Day off removed");
                                                }}
                                            />
                                        </div>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </section>

                    {/* Special Days */}
                    <section className="rounded-2xl glass p-5" data-testid="special-days-section">
                        <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
                            <div>
                                <div className="text-[10px] uppercase tracking-[0.24em] text-white/45 mb-1 flex items-center gap-2">
                                    <Clock className="h-3 w-3 text-gold" strokeWidth={1.5} /> One-off hours
                                </div>
                                <h2 className="font-serif text-2xl text-white">Change hours for one day</h2>
                                <p className="text-[11px] text-white/50 mt-1">Open on a normally-closed day, finish early, or run a limited-hours special.</p>
                            </div>
                            <button onClick={() => { setSpecialEditing(null); setSpecialOpen(true); }}
                                    data-testid="add-special-day-btn"
                                    className="h-11 px-5 rounded-full bg-gold text-navy font-semibold text-sm inline-flex items-center gap-2">
                                <Plus className="h-4 w-4" /> Change hours for this date
                            </button>
                        </div>
                        {specialDays.length === 0 ? (
                            <div className="text-sm text-white/50" data-testid="special-days-empty">No special hours scheduled.</div>
                        ) : (
                            <ul className="space-y-2">
                                {specialDays.map((s) => (
                                    <li key={s.id} data-testid={`special-day-${s.id}`}
                                        className="rounded-xl border hairline p-3 bg-white/[0.02]">
                                        <div className="flex items-center justify-between gap-2">
                                            <div className="min-w-0 flex-1">
                                                <div className="font-ui text-white text-sm">
                                                    {s.startDate === s.endDate ? fmtDate(s.startDate) : `${fmtDate(s.startDate)} → ${fmtDate(s.endDate)}`}
                                                </div>
                                                <ul className="text-[11px] text-white/60 mt-1 space-y-0.5">
                                                    {(s.periodList || []).map((p, i) => (
                                                        <li key={i}>{trimTime(p.startTime)} — {trimTime(p.endTime)}</li>
                                                    ))}
                                                </ul>
                                            </div>
                                            <div className="flex gap-1">
                                                <button onClick={() => { setSpecialEditing(s); setSpecialOpen(true); }}
                                                        data-testid={`special-day-edit-${s.id}`}
                                                        className="h-8 w-8 rounded-full border hairline text-white/70 hover:text-gold hover:border-gold/40 inline-flex items-center justify-center">
                                                    <Pencil className="h-3 w-3" strokeWidth={1.5} />
                                                </button>
                                                <DeleteButton
                                                    testId={`special-day-delete-${s.id}`}
                                                    confirmText="Remove these special hours?"
                                                    onConfirm={async () => {
                                                        const r = await staffApi.availDeleteSpecialDay(employeeId, s.id);
                                                        applyResult(r); toast.success("Special hours removed");
                                                    }}
                                                />
                                            </div>
                                        </div>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </section>
                </>
            )}

            {/* Dialogs */}
            {dayOffOpen && (
                <DayOffDialog
                    employeeId={employeeId}
                    existing={dayOffEditing}
                    onClose={() => setDayOffOpen(false)}
                    onSaved={applyResult}
                />
            )}
            {specialOpen && (
                <SpecialDayDialog
                    employeeId={employeeId}
                    existing={specialEditing}
                    onClose={() => setSpecialOpen(false)}
                    onSaved={applyResult}
                />
            )}
            {weekDayOpen && weekDayEditing && (
                <WeekDayDialog
                    employeeId={employeeId}
                    dayIndex={weekDayEditing.dayIndex}
                    existing={weekDayEditing.existing}
                    onClose={() => setWeekDayOpen(false)}
                    onSaved={applyResult}
                />
            )}
        </div>
    );
}

/* ==========================================================================
 * Delete confirmation button — inline confirm, no browser confirm()
 * ======================================================================= */
function DeleteButton({ testId, confirmText, onConfirm }) {
    const [confirming, setConfirming] = useState(false);
    const [busy, setBusy] = useState(false);
    if (confirming) {
        return (
            <div className="flex items-center gap-1 rounded-full border border-red-400/40 bg-red-500/10 pl-2 pr-1 h-8" data-testid={`${testId}-confirm`}>
                <span className="text-[11px] text-red-100 whitespace-nowrap">{confirmText}</span>
                <button onClick={async () => {
                    setBusy(true);
                    try { await onConfirm(); }
                    catch (e) { toast.error(e?.message || "Delete failed"); }
                    finally { setBusy(false); setConfirming(false); }
                }} disabled={busy}
                        className="h-6 w-6 rounded-full bg-red-500 text-white text-[10px] font-bold inline-flex items-center justify-center disabled:opacity-40">
                    ✓
                </button>
                <button onClick={() => setConfirming(false)} disabled={busy}
                        className="h-6 w-6 rounded-full text-white/80 inline-flex items-center justify-center">
                    <X className="h-3 w-3" />
                </button>
            </div>
        );
    }
    return (
        <button onClick={() => setConfirming(true)} data-testid={testId}
                className="h-8 w-8 rounded-full border hairline text-red-300 hover:border-red-300/50 inline-flex items-center justify-center">
            <Trash2 className="h-3 w-3" strokeWidth={1.5} />
        </button>
    );
}

/* ==========================================================================
 * Reusable modal shell
 * ======================================================================= */
function Modal({ title, onClose, children, testId }) {
    return (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-end sm:items-center justify-center" onClick={onClose}>
            <div onClick={(e) => e.stopPropagation()} data-testid={testId}
                 className="w-[calc(100%_-_2rem)] min-w-0 sm:max-w-lg m-4 rounded-2xl glass-strong border hairline p-5 space-y-4 max-h-[calc(100dvh-2rem)] overflow-y-auto">
                <div className="flex items-center justify-between">
                    <h3 className="font-serif text-2xl text-white">{title}</h3>
                    <button onClick={onClose} className="h-9 w-9 rounded-full border hairline text-white/70 hover:text-white inline-flex items-center justify-center" data-testid="modal-close">
                        <X className="h-4 w-4" />
                    </button>
                </div>
                {children}
            </div>
        </div>
    );
}

/* ==========================================================================
 * Conflict warning panel
 * ======================================================================= */
function ConflictsPanel({ conflicts, message, onCancel, onForce, busy }) {
    return (
        <div className="rounded-xl border border-amber-400/40 bg-amber-500/5 p-3 space-y-2" data-testid="conflicts-warning">
            <div className="flex items-start gap-2">
                <AlertTriangle className="h-4 w-4 text-amber-300 shrink-0 mt-0.5" strokeWidth={2} />
                <div className="min-w-0 flex-1">
                    <p className="text-sm text-amber-100">{message || `${conflicts.length} existing appointment(s) fall in this range.`}</p>
                    <ul className="mt-2 space-y-1 max-h-40 overflow-y-auto">
                        {conflicts.slice(0, 8).map((a) => (
                            <li key={a.id} className="text-[11px] text-white/75">
                                <span className="text-white">{a.customerName || "Customer"}</span>
                                <span className="text-white/50"> · {a.serviceName}</span>
                                <span className="text-white/50"> · {new Date(a.bookingStart).toLocaleString("en-GB", { dateStyle: "medium", timeStyle: "short" })}</span>
                            </li>
                        ))}
                        {conflicts.length > 8 && <li className="text-[11px] text-white/50">…and {conflicts.length - 8} more</li>}
                    </ul>
                    <p className="text-[11px] text-amber-100/80 mt-2">
                        Existing bookings will not be cancelled automatically. Move or cancel them from the Appointments tab if needed.
                    </p>
                </div>
            </div>
            <div className="flex gap-2 pt-1">
                <button onClick={onCancel} disabled={busy}
                        className="flex-1 h-10 rounded-full border hairline text-white/80 text-sm" data-testid="conflict-cancel">
                    Cancel
                </button>
                <button onClick={onForce} disabled={busy} data-testid="conflict-continue"
                        className="flex-1 h-10 rounded-full bg-amber-500 text-navy font-semibold text-sm inline-flex items-center justify-center gap-1">
                    Continue anyway
                </button>
            </div>
        </div>
    );
}

/* ==========================================================================
 * Day off dialog (add/edit)
 * ======================================================================= */
function DayOffDialog({ employeeId, existing, onClose, onSaved }) {
    const [name, setName] = useState(existing?.name || "");
    const [startDate, setStartDate] = useState(existing?.startDate || todayISO());
    const [endDate, setEndDate] = useState(existing?.endDate || existing?.startDate || todayISO());
    const [repeat, setRepeat] = useState(!!existing?.repeat);
    const [busy, setBusy] = useState(false);
    const [pending, setPending] = useState(null); // { conflicts, message } awaiting confirmation

    const save = async (force = false) => {
        if (!name.trim()) { toast.error("Please give this day off a name."); return; }
        if (endDate < startDate) { toast.error("End date must be on or after the start date."); return; }
        setBusy(true);
        try {
            const payload = { name: name.trim(), startDate, endDate, repeat };
            const r = existing
                ? await staffApi.availUpdateDayOff(employeeId, existing.id, payload, force)
                : await staffApi.availAddDayOff(employeeId, payload, force);
            if (r.requires_confirmation) { setPending(r); setBusy(false); return; }
            onSaved(r);
            toast.success(existing ? "Day off updated" : "Day off saved");
            onClose();
        } catch (e) { toast.error(e?.message || "Save failed"); setBusy(false); }
    };

    return (
        <Modal title={existing ? "Edit day off" : "Block entire day"} onClose={onClose} testId="day-off-modal">
            {pending ? (
                <ConflictsPanel {...pending} busy={busy}
                    onCancel={() => setPending(null)}
                    onForce={() => save(true)} />
            ) : (
                <div className="space-y-3">
                    <label className="block">
                        <div className="text-[10px] uppercase tracking-[0.18em] text-white/50 mb-1">Reason / name</div>
                        <input value={name} onChange={(e) => setName(e.target.value)} autoFocus
                               data-testid="day-off-name" placeholder="e.g. Night away, Vacation, Training"
                               className="w-full h-11 bg-transparent border hairline rounded-md px-3 text-white text-sm outline-none focus:border-gold" />
                    </label>
                    <div className="grid grid-cols-2 gap-2">
                        <label className="block">
                            <div className="text-[10px] uppercase tracking-[0.18em] text-white/50 mb-1">Start date</div>
                            <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)}
                                   data-testid="day-off-start" min={todayISO()}
                                   className="w-full h-11 bg-transparent border hairline rounded-md px-3 text-white text-sm outline-none focus:border-gold" />
                        </label>
                        <label className="block">
                            <div className="text-[10px] uppercase tracking-[0.18em] text-white/50 mb-1">End date</div>
                            <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)}
                                   data-testid="day-off-end" min={startDate}
                                   className="w-full h-11 bg-transparent border hairline rounded-md px-3 text-white text-sm outline-none focus:border-gold" />
                        </label>
                    </div>
                    <label className="flex items-center gap-2 text-sm text-white/70">
                        <input type="checkbox" checked={repeat} onChange={(e) => setRepeat(e.target.checked)}
                               data-testid="day-off-repeat" className="accent-gold h-4 w-4" />
                        Repeat every year (e.g. birthday, annual holiday)
                    </label>
                    <div className="flex gap-2 pt-2">
                        <button onClick={onClose} disabled={busy}
                                className="flex-1 h-11 rounded-full border hairline text-white/80 text-sm">Cancel</button>
                        <button onClick={() => save(false)} disabled={busy || !name.trim()}
                                data-testid="day-off-save"
                                className="flex-1 h-11 rounded-full bg-gold text-navy font-semibold text-sm inline-flex items-center justify-center gap-1">
                            <Save className="h-4 w-4" /> {existing ? "Update" : "Block day"}
                        </button>
                    </div>
                </div>
            )}
        </Modal>
    );
}

/* ==========================================================================
 * Special day dialog (add/edit)
 * ======================================================================= */
function SpecialDayDialog({ employeeId, existing, onClose, onSaved }) {
    const initialPeriods = (existing?.periodList || []).map((p) => ({ startTime: trimTime(p.startTime), endTime: trimTime(p.endTime) }));
    const [startDate, setStartDate] = useState(existing?.startDate || todayISO());
    const [endDate, setEndDate] = useState(existing?.endDate || existing?.startDate || todayISO());
    const [periods, setPeriods] = useState(initialPeriods.length ? initialPeriods : [{ startTime: "10:00", endTime: "14:00" }]);
    const [busy, setBusy] = useState(false);
    const [pending, setPending] = useState(null);

    const updatePeriod = (i, patch) => setPeriods((prev) => prev.map((p, idx) => idx === i ? { ...p, ...patch } : p));
    const addPeriod = () => setPeriods((prev) => [...prev, { startTime: "14:00", endTime: "16:00" }]);
    const removePeriod = (i) => setPeriods((prev) => prev.filter((_, idx) => idx !== i));

    const save = async (force = false) => {
        if (endDate < startDate) { toast.error("End date must be on or after the start date."); return; }
        if (!periods.length) { toast.error("Add at least one working period."); return; }
        for (const p of periods) {
            if (!p.startTime || !p.endTime || p.endTime <= p.startTime) {
                toast.error("Each working period needs a start time before its end time."); return;
            }
        }
        setBusy(true);
        try {
            const payload = { startDate, endDate, periodList: periods };
            const r = existing
                ? await staffApi.availUpdateSpecialDay(employeeId, existing.id, payload, force)
                : await staffApi.availAddSpecialDay(employeeId, payload, force);
            if (r.requires_confirmation) { setPending(r); setBusy(false); return; }
            onSaved(r);
            toast.success(existing ? "Special hours updated" : "Special hours saved");
            onClose();
        } catch (e) { toast.error(e?.message || "Save failed"); setBusy(false); }
    };

    return (
        <Modal title={existing ? "Edit special hours" : "Change hours for this date"} onClose={onClose} testId="special-day-modal">
            {pending ? (
                <ConflictsPanel {...pending} busy={busy}
                    onCancel={() => setPending(null)}
                    onForce={() => save(true)} />
            ) : (
                <div className="space-y-3">
                    <div className="grid grid-cols-2 gap-2">
                        <label className="block">
                            <div className="text-[10px] uppercase tracking-[0.18em] text-white/50 mb-1">Start date</div>
                            <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)}
                                   data-testid="special-day-start" min={todayISO()}
                                   className="w-full h-11 bg-transparent border hairline rounded-md px-3 text-white text-sm outline-none focus:border-gold" />
                        </label>
                        <label className="block">
                            <div className="text-[10px] uppercase tracking-[0.18em] text-white/50 mb-1">End date</div>
                            <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)}
                                   data-testid="special-day-end" min={startDate}
                                   className="w-full h-11 bg-transparent border hairline rounded-md px-3 text-white text-sm outline-none focus:border-gold" />
                        </label>
                    </div>
                    <div>
                        <div className="text-[10px] uppercase tracking-[0.18em] text-white/50 mb-2">Working periods</div>
                        <div className="space-y-2">
                            {periods.map((p, i) => (
                                <div key={i} className="flex gap-1 items-center" data-testid={`special-period-${i}`}>
                                    <TimeInput value={p.startTime} onChange={(v) => updatePeriod(i, { startTime: v })} testId={`special-period-${i}-start`} />
                                    <span className="hidden sm:block text-white/40 text-sm">→</span>
                                    <TimeInput value={p.endTime} onChange={(v) => updatePeriod(i, { endTime: v })} testId={`special-period-${i}-end`} />
                                    {periods.length > 1 && (
                                        <button onClick={() => removePeriod(i)} data-testid={`special-period-${i}-remove`}
                                                className="h-9 w-9 rounded-full text-red-300 hover:bg-red-500/10 inline-flex items-center justify-center">
                                            <Trash2 className="h-3 w-3" />
                                        </button>
                                    )}
                                </div>
                            ))}
                            <button onClick={addPeriod} data-testid="special-period-add"
                                    className="h-9 px-3 rounded-full border hairline text-xs text-white/70 hover:text-gold hover:border-gold/40 inline-flex items-center gap-1">
                                <Plus className="h-3 w-3" /> Add period
                            </button>
                        </div>
                    </div>
                    <div className="rounded-lg bg-white/[0.03] p-2 text-[11px] text-white/60 leading-relaxed">
                        <Info className="h-3 w-3 text-gold/70 inline-block mr-1" /> On the day, only these hours are bookable. Add a second period to split for a lunch break.
                    </div>
                    <div className="flex gap-2 pt-2">
                        <button onClick={onClose} disabled={busy}
                                className="flex-1 h-11 rounded-full border hairline text-white/80 text-sm">Cancel</button>
                        <button onClick={() => save(false)} disabled={busy} data-testid="special-day-save"
                                className="flex-1 h-11 rounded-full bg-gold text-navy font-semibold text-sm inline-flex items-center justify-center gap-1">
                            <Save className="h-4 w-4" /> {existing ? "Update" : "Save"}
                        </button>
                    </div>
                </div>
            )}
        </Modal>
    );
}

/* ==========================================================================
 * Weekday hours dialog (edit or enable / disable a weekday)
 * ======================================================================= */
function WeekDayDialog({ employeeId, dayIndex, existing, onClose, onSaved }) {
    const [enabled, setEnabled] = useState(!!existing);
    const [startTime, setStartTime] = useState(trimTime(existing?.startTime) || "10:00");
    const [endTime, setEndTime] = useState(trimTime(existing?.endTime) || "18:00");
    const initialPeriods = (existing?.periodList || []).map((p) => ({ startTime: trimTime(p.startTime), endTime: trimTime(p.endTime) }));
    const initialBreaks  = (existing?.timeOutList  || []).map((t) => ({ startTime: trimTime(t.startTime), endTime: trimTime(t.endTime) }));
    const [periods, setPeriods] = useState(initialPeriods.length ? initialPeriods : [{ startTime: "10:00", endTime: "18:00" }]);
    const [breaks, setBreaks]   = useState(initialBreaks);
    const [busy, setBusy] = useState(false);

    const updatePeriod = (i, patch) => setPeriods((prev) => prev.map((p, idx) => idx === i ? { ...p, ...patch } : p));
    const addPeriod = () => setPeriods((prev) => [...prev, { startTime: "14:00", endTime: "18:00" }]);
    const removePeriod = (i) => setPeriods((prev) => prev.filter((_, idx) => idx !== i));
    const updateBreak = (i, patch) => setBreaks((prev) => prev.map((b, idx) => idx === i ? { ...b, ...patch } : b));
    const addBreak = () => setBreaks((prev) => [...prev, { startTime: "13:00", endTime: "13:30" }]);
    const removeBreak = (i) => setBreaks((prev) => prev.filter((_, idx) => idx !== i));

    const disableDay = async () => {
        if (!window.confirm(`Turn off ${DAY_NAMES[dayIndex]}? The day will be hidden from customer booking.`)) return;
        setBusy(true);
        try {
            const r = await staffApi.availUpsertWeekDay(employeeId, { dayIndex, enabled: false });
            onSaved(r);
            toast.success(`${DAY_NAMES[dayIndex]} closed`);
            onClose();
        } catch (e) { toast.error(e?.message || "Update failed"); setBusy(false); }
    };

    const save = async () => {
        if (!enabled) return disableDay();
        if (endTime <= startTime) { toast.error("Day end time must be after start time."); return; }
        for (const p of periods) {
            if (!p.startTime || !p.endTime || p.endTime <= p.startTime) {
                toast.error("Each period needs a start time before its end time."); return;
            }
        }
        setBusy(true);
        try {
            const r = await staffApi.availUpsertWeekDay(employeeId, {
                dayIndex, enabled: true, startTime, endTime,
                periodList: periods, timeOutList: breaks,
            });
            onSaved(r);
            toast.success(`${DAY_NAMES[dayIndex]} hours saved`);
            onClose();
        } catch (e) { toast.error(e?.message || "Update failed"); setBusy(false); }
    };

    return (
        <Modal title={`${DAY_NAMES[dayIndex]} hours`} onClose={onClose} testId="week-day-modal">
            <div className="space-y-4">
                <label className="flex items-center gap-3 rounded-xl border hairline p-3 bg-white/[0.02] cursor-pointer">
                    <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)}
                           data-testid="weekday-enabled" className="accent-gold h-4 w-4" />
                    <div className="flex-1">
                        <div className="text-sm text-white">Open on {DAY_NAMES[dayIndex]}</div>
                        <div className="text-[11px] text-white/50 mt-0.5">Untick to close this day for customer bookings.</div>
                    </div>
                </label>
                {enabled && (
                    <>
                        <div className="grid grid-cols-2 gap-2">
                            <label className="block">
                                <div className="text-[10px] uppercase tracking-[0.18em] text-white/50 mb-1">Start</div>
                                <TimeInput value={startTime} onChange={setStartTime} testId="weekday-start" />
                            </label>
                            <label className="block">
                                <div className="text-[10px] uppercase tracking-[0.18em] text-white/50 mb-1">Finish</div>
                                <TimeInput value={endTime} onChange={setEndTime} testId="weekday-end" />
                            </label>
                        </div>
                        <div>
                            <div className="text-[10px] uppercase tracking-[0.18em] text-white/50 mb-2 flex items-center gap-2">
                                <Clock className="h-3 w-3 text-gold/60" /> Working periods
                            </div>
                            <div className="space-y-2">
                                {periods.map((p, i) => (
                                    <div key={i} className="grid grid-cols-1 sm:grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)_auto] gap-2 items-center rounded-xl border border-white/10 p-3" data-testid={`weekday-period-${i}`}>
                                        <TimeInput value={p.startTime} onChange={(v) => updatePeriod(i, { startTime: v })} testId={`weekday-period-${i}-start`} />
                                        <span className="hidden sm:block text-white/40 text-sm">→</span>
                                        <TimeInput value={p.endTime}   onChange={(v) => updatePeriod(i, { endTime: v })}   testId={`weekday-period-${i}-end`} />
                                        {periods.length > 1 && (
                                            <button onClick={() => removePeriod(i)} data-testid={`weekday-period-${i}-remove`}
                                                    className="h-9 w-9 rounded-full text-red-300 hover:bg-red-500/10 inline-flex items-center justify-center">
                                                <Trash2 className="h-3 w-3" />
                                            </button>
                                        )}
                                    </div>
                                ))}
                                <button onClick={addPeriod} data-testid="weekday-period-add"
                                        className="h-9 px-3 rounded-full border hairline text-xs text-white/70 hover:text-gold hover:border-gold/40 inline-flex items-center gap-1">
                                    <Plus className="h-3 w-3" /> Add period
                                </button>
                            </div>
                        </div>
                        <div>
                            <div className="text-[10px] uppercase tracking-[0.18em] text-white/50 mb-2 flex items-center gap-2">
                                <Coffee className="h-3 w-3 text-gold/60" /> Breaks & lunch
                            </div>
                            <div className="space-y-2">
                                {breaks.map((b, i) => (
                                    <div key={i} className="grid grid-cols-1 sm:grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)_auto] gap-2 items-center rounded-xl border border-white/10 p-3" data-testid={`weekday-break-${i}`}>
                                        <TimeInput value={b.startTime} onChange={(v) => updateBreak(i, { startTime: v })} testId={`weekday-break-${i}-start`} />
                                        <span className="hidden sm:block text-white/40 text-sm">→</span>
                                        <TimeInput value={b.endTime}   onChange={(v) => updateBreak(i, { endTime: v })}   testId={`weekday-break-${i}-end`} />
                                        <button onClick={() => removeBreak(i)} data-testid={`weekday-break-${i}-remove`}
                                                className="h-9 w-9 rounded-full text-red-300 hover:bg-red-500/10 inline-flex items-center justify-center">
                                            <Trash2 className="h-3 w-3" />
                                        </button>
                                    </div>
                                ))}
                                <button onClick={addBreak} data-testid="weekday-break-add"
                                        className="h-9 px-3 rounded-full border hairline text-xs text-white/70 hover:text-gold hover:border-gold/40 inline-flex items-center gap-1">
                                    <Plus className="h-3 w-3" /> Add break
                                </button>
                            </div>
                        </div>
                        {existing && (existing.periodList || []).some((p) => (p.periodServiceList || []).length > 0) && (
                            <div className="rounded-lg bg-white/[0.03] p-2 text-[11px] text-white/60 leading-relaxed">
                                <Info className="h-3 w-3 text-gold/70 inline-block mr-1" /> This day has custom service assignments. They will be preserved on save.
                            </div>
                        )}
                    </>
                )}
                <div className="flex gap-2 pt-2">
                    <button onClick={onClose} disabled={busy}
                            className="flex-1 h-11 rounded-full border hairline text-white/80 text-sm">Cancel</button>
                    <button onClick={save} disabled={busy} data-testid="weekday-save"
                            className="flex-1 h-11 rounded-full bg-gold text-navy font-semibold text-sm inline-flex items-center justify-center gap-1">
                        <Save className="h-4 w-4" /> {enabled ? "Save hours" : "Close this day"}
                    </button>
                </div>
            </div>
        </Modal>
    );
}

function TimeInput({ value, onChange, testId }) {
    return <input type="time" value={value || ""} step={60}
        aria-label={testId.includes('end') ? 'Finish time' : 'Start time'}
        data-testid={testId} onChange={event => onChange(event.target.value)}
        onClick={event => { try { event.currentTarget.showPicker?.(); } catch (_) { /* Native editing remains available. */ } }}
        style={{colorScheme:'light', minHeight:48}}
        className="block w-full min-w-0 max-w-full rounded-lg border bg-white px-3 py-3 text-navy text-base" />;
}
